import { TypeGuards } from '@codeleap/common'
import { GlobalState } from '@codeleap/store'
import { useCallback, useEffect, useState } from 'react'

/**
 * How long a video can sit with no `onReady`/`onLoad` and no reported error before it's
 * treated as stalled. Set to 3s per the "Unhandled infinite video stream loading freeze" bug
 * report (Kite Tracker), whose expected result caps this at 3 seconds.
 */
export const loadStallTimeout = 3000

type Params<Meta, Err> = {
  metadataStore: GlobalState<Meta>
  errorStore: GlobalState<Err>
}

/**
 * A stalled stream never raises an error and never raises `onReady`, so nothing else here
 * ever learns it happened — `Placeholder` reads `metadataStore` being empty as "still
 * loading" and spins forever. This gives that wait a deadline: past `loadStallTimeout` with
 * no load and no error, it is treated as failed rather than pending.
 *
 * `attempt` keys the `ReactPlayer` (see `Immersive.tsx`) so a retry is a real remount, not a
 * repaint — the player only requests the source it was given when it mounted, so clearing
 * state without recreating it would leave the same stuck instance in place.
 */
export function useVideoStallRecovery<Meta, Err>({ metadataStore, errorStore }: Params<Meta, Err>) {
  const [attempt, setAttempt] = useState(0)
  const [stalled, setStalled] = useState(false)

  const isLoading = metadataStore.use(s => TypeGuards.isNil(s))
  const hasError = errorStore.use(s => !TypeGuards.isNil(s))

  useEffect(() => {
    // A load that succeeds on its own after the deadline already fired (no retry involved)
    // must still clear `stalled` — otherwise the error overlay outlives the failure it was
    // reporting and sits over a video that is now playing fine.
    if (!isLoading) {
      setStalled(false)
      return
    }
    if (hasError) return

    const timer = setTimeout(() => setStalled(true), loadStallTimeout)
    return () => clearTimeout(timer)
    // `attempt` restarts the deadline on retry even though `isLoading` was already `true` —
    // it never changed, so it alone can't be what reruns this effect.
  }, [isLoading, hasError, attempt])

  const retry = useCallback(() => {
    metadataStore.set(null)
    errorStore.set(null)
    setStalled(false)
    setAttempt(a => a + 1)
  }, [metadataStore, errorStore])

  return { stalled, attempt, retry }
}
